/**
 * 图片放大查看
 * -----------------------------------------------------------------------------
 * 证件照 / 证件头像的缩略图最大只有 64~96px，学员看不出「头发有没有出框」
 * 「脸有没有偏」，而这正是他需要当场判断的两件事。点击放大是最小成本的解法。
 *
 * 用 `ImageViewer.Multi` 而不是单图 `show`：证件照页面上同时存在「成品」与
 * 「证件头像」两张图时，可以左右滑动对照（`images` 传全部、`defaultIndex`
 * 定位到被点的那张）。只传一张时行为与单图一致，不必写两个分支。
 *
 * ⚠️ 只给**独立成块的 `<img>`** 用：身份证正反面那两格本身就是「点击重拍」的
 * 触发器（`button`），在里面再套放大入口会变成 button 套 button，且两种点击
 * 语义打架 —— 那一格保持「点了就是重拍」。
 *
 * 返回手势（2026-10-26 真机反馈）
 * -----------------------------------------------------------------------------
 * 原来浮层由 `renderImperatively` 渲染到 body 上，是**不随路由卸载**的独立节点。
 * 学员习惯性用全面屏左缘右滑想关掉大图，结果路由退了、图还挂在屏幕上 ——
 * 变成「一张关不掉的图盖在一个已经返回了的页面上面」。
 *
 * 现在给浮层压一层返回守卫（`lib/back-guard.ts`）：手势返回只关图，页面不动。
 */
import { ImageViewer } from 'antd-mobile';
import { type BackGuard, pushBackGuard } from '../lib/back-guard';

/** 当前已展开的那一张（同一时刻只允许一层，否则历史里会叠两条哨兵） */
let activeClose: (() => void) | null = null;

export function openImagePreview(url: string | null | undefined, group?: string[]): void {
  if (!url) return;

  // 上一张还没关就又点了一张：先收掉，否则会压两条哨兵
  activeClose?.();

  const images = group?.length ? group.filter(Boolean) : [url];
  const index = images.indexOf(url);

  let finished = false;
  let guard: BackGuard | null = null;

  const finish = (): void => {
    if (finished) return;
    finished = true;
    activeClose = null;
    // 先摘守卫（哨兵还在就顺手弹掉），再关浮层
    guard?.release(true);
    guard = null;
    handler.close();
  };

  const handler = ImageViewer.Multi.show({
    images,
    defaultIndex: index >= 0 ? index : 0,
    // 点遮罩 / 关闭按钮：与手势返回走同一条收尾路径
    onClose: () => finish(),
  });

  activeClose = finish;
  guard = pushBackGuard(() => {
    // 手势返回 = 取消查看。哨兵已被弹回一位，正好是打开图片之前的状态，
    // 所以返回 `dismiss`：不跳路由，也不再动历史。
    finish();
    return 'dismiss';
  });
}
